import React from 'react'
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Space,
  Typography,
} from '@surecompanies/core_components'
import {
  DownloadOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import {
  useAllowedBulkStatuses,
  ALL_FIELD_KEYS,
  CHUNK_SIZE,
  MAX_CSV_ROWS,
  fieldCsvHeader,
} from './bulkStatus.constants'
import { STATUS_FIELD_CONFIG } from '../PaymentAssignment/CaseManagement/statusUpdate.type'
import { getPaymentAssignmentStatusLabel } from '../PaymentAssignment/Utils/types'

const { Title, Text } = Typography

interface UploadStepProps {
  period: Dayjs
  parseError: string | null
  fileInputRef: React.RefObject<HTMLInputElement>
  onPeriodChange: (d: Dayjs) => void
  onDownloadTemplate: () => void
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onAddManually: () => void
}

export function UploadStep({
  period,
  parseError,
  fileInputRef,
  onPeriodChange,
  onDownloadTemplate,
  onFileChange,
  onAddManually,
}: UploadStepProps) {
  const allowedStatuses = useAllowedBulkStatuses()
  return (
    <Card style={{ padding: '24px' }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Title level={4} style={{ margin: 0 }}>
          Bulk Update Payment Assignment Status
        </Title>
        <Text type="secondary">
          Upload a CSV of benefit IDs with the target status and optional
          status-specific fields for the selected payment period.
        </Text>

        {parseError && (
          <Alert type="error" message={parseError} showIcon closable />
        )}

        <Space wrap align="center">
          <Text strong>Payment period:</Text>
          <DatePicker
            picker="month"
            value={period}
            onChange={(value: Dayjs | null) => {
              if (value) onPeriodChange(value.startOf('month'))
            }}
            format="MMMM YYYY"
            allowClear={false}
            style={{ minWidth: 180 }}
          />
        </Space>

        <Space wrap>
          <Button icon={<DownloadOutlined />} onClick={onDownloadTemplate}>
            Download CSV Template
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={onFileChange}
          />
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload CSV
          </Button>
          <Button icon={<PlusOutlined />} onClick={onAddManually}>
            Add Manually
          </Button>
        </Space>

        <Card
          size="small"
          style={{ background: '#fafafa', borderStyle: 'dashed' }}
        >
          <Text strong>CSV format</Text>

          {/* Required columns */}
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" strong>
              Required columns:
            </Text>
            <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 20 }}>
              <li>
                <Text code>benefitId</Text>
                <Text type="secondary">
                  {' '}
                  — benefit ID example: 2045ea56-3e79-4562-9011-513258eb9e10
                  (required)
                </Text>
              </li>
              <li>
                <Text code>status</Text>
                <Text type="secondary">
                  {' '}
                  — one of the status values below (required)
                </Text>
              </li>
              <li>
                <Text code>note</Text>
                <Text type="secondary"> — free-text note (optional)</Text>
              </li>
              <li>
                <Text type="secondary">
                  Any of the status-specific field columns listed below (leave
                  blank if not applicable)
                </Text>
              </li>
            </ul>
          </div>

          {/* Status table */}
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" strong>
              Available statuses &amp; required fields:
            </Text>
            <table
              style={{
                marginTop: 6,
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ background: '#e8e8e8' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '4px 8px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    Label
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '4px 8px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    <Text code style={{ fontSize: 11 }}>
                      status
                    </Text>{' '}
                    value
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '4px 8px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    Required fields
                  </th>
                </tr>
              </thead>
              <tbody>
                {allowedStatuses.map((s, i) => {
                  const rules = STATUS_FIELD_CONFIG[s] ?? []
                  const requiredRules = rules.filter(
                    (r) => r.required && !r.when
                  )
                  return (
                    <tr
                      key={s}
                      style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                    >
                      <td
                        style={{
                          padding: '3px 8px',
                          border: '1px solid #d9d9d9',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getPaymentAssignmentStatusLabel(s)}
                      </td>
                      <td
                        style={{
                          padding: '3px 8px',
                          border: '1px solid #d9d9d9',
                        }}
                      >
                        <Text code style={{ fontSize: 11 }}>
                          {s}
                        </Text>
                      </td>
                      <td
                        style={{
                          padding: '3px 8px',
                          border: '1px solid #d9d9d9',
                          color: '#595959',
                        }}
                      >
                        {requiredRules.length === 0
                          ? '—'
                          : requiredRules
                              .map((r) => fieldCsvHeader(r.key))
                              .join(', ')}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Field descriptions */}
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" strong>
              Status-specific field columns:
            </Text>
            <table
              style={{
                marginTop: 6,
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ background: '#e8e8e8' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '4px 8px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    Column header
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '4px 8px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    Type / allowed values
                  </th>
                </tr>
              </thead>
              <tbody>
                {ALL_FIELD_KEYS.map((key, i) => {
                  // Find a rule for this key to get its type and options
                  const rule = allowedStatuses
                    .flatMap((s) => STATUS_FIELD_CONFIG[s] ?? [])
                    .find((r) => r.key === key)
                  const typeLabel = rule?.options
                    ? rule.options.join(' / ')
                    : rule?.type === 'date'
                      ? 'MM-DD-YYYY or MM/DD/YYYY'
                      : rule?.type === 'number'
                        ? 'number'
                        : rule?.type === 'day'
                          ? '1 – 31'
                          : 'text'
                  return (
                    <tr
                      key={key}
                      style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                    >
                      <td
                        style={{
                          padding: '3px 8px',
                          border: '1px solid #d9d9d9',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Text code style={{ fontSize: 11 }}>
                          {fieldCsvHeader(key)}
                        </Text>
                      </td>
                      <td
                        style={{
                          padding: '3px 8px',
                          border: '1px solid #d9d9d9',
                          color: '#595959',
                        }}
                      >
                        {typeLabel}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Batches of {CHUNK_SIZE}. Maximum {MAX_CSV_ROWS.toLocaleString()}{' '}
            rows.
          </Text>
        </Card>
      </Space>
    </Card>
  )
}
